/**
 * S0 local fence gate (PC-B rev 9, section 10 + 10b.2).
 *
 * The device-local fail-closed gate for every keepsake operation:
 * preview, export, poll, status, and delivery all fail closed while ANY
 * mutation fence has unequal sequences, an in-flight sequence, an owner
 * mismatch, a missing/unresolved corpus fence, or any unresolved
 * validity intent. Because the pre-S1 store cannot prove which owner a
 * row belongs to, the gate never selectively blesses pending rows.
 *
 * Also owns the async payload-digest finalizer injected into db.ts (a
 * WebCrypto hash cannot run inside an IDB transaction; the conditional
 * patch keys on the exact sequence so a later mutation recomputes for
 * its own sequence and an unpatched digest simply cannot acknowledge).
 */

import * as db from '@/lib/db';
import type { Prayer } from '@/types/prayer';
import {
  broadcastLocalPrayerEpoch,
  getAuthEpoch,
  getAuthUserId,
  initKeepsakeEpochs,
} from './epochs';
import { computePrayerSourceDigest } from './source';
import { decimalCompare } from './types';

let wired = false;

/** Boot wiring (main.tsx): epochs + the db.ts injection points. */
export function initKeepsakeFences(): void {
  if (wired) return;
  wired = true;
  initKeepsakeEpochs();
  db._setAuthEpochSource(getAuthEpoch);
  db._setEpochBroadcast(broadcastLocalPrayerEpoch);
  db._setFenceDigestFinalizer((prayerId, seq) => {
    void finalizeFenceDigest(prayerId, seq);
  });
}

/**
 * Post-write digest finalizer: recompute the exact post-mutation
 * KeepsakePrayerSourceV1 digest for an OWNER-SCOPED fence and patch it
 * conditionally on the same sequence. Owner-ambiguous fences (ownerScope
 * null, the pre-R4 state) record null and cannot acknowledge.
 */
async function finalizeFenceDigest(prayerId: string, seq: string): Promise<void> {
  try {
    const fence = await db.getKeepsakeFence(prayerId);
    if (!fence || fence.localMutationSeq !== seq) return;
    if (fence.ownerScope === null) return; // stays null; cannot dispatch ack
    const prayer = await db.getPrayer(prayerId);
    if (!prayer) return;
    const digest = await computePrayerSourceDigest(fence.ownerScope, prayer);
    await db.patchKeepsakeFenceDigest(prayerId, seq, digest);
  } catch {
    // Fail closed: an uncomputable digest leaves the fence unmatched.
  }
}

export type KeepsakeHold =
  | { held: false }
  | { held: true; reason: 'protection-pending' | 'sync-pending' };

/**
 * The mandatory durable reread (PC-B section 10): one IDB pass over the
 * durable epoch, every mutation fence, the corpus fence, and the
 * validity journal. Called on focus, pageshow, BFCache restore, and
 * immediately before accepting ANY preview/export/poll/status/delivery
 * result. Returns the hold state + the epoch observed.
 */
export async function readKeepsakeGate(): Promise<{
  hold: KeepsakeHold;
  localPrayerEpoch: string;
}> {
  // ONE readonly IDB transaction over epoch + fences + corpus fence +
  // journal (the spec's mandatory single-transaction reread).
  const { epoch, fences, intents } = await db.readKeepsakeGateSnapshot();

  // Any unresolved validity intent blocks (protection pending).
  const unresolvedIntent = intents.some(
    (i) => i.state === 'local-only' || i.state === 'in-flight' || i.state === 'rejected',
  );
  if (unresolvedIntent) {
    return { hold: { held: true, reason: 'protection-pending' }, localPrayerEpoch: epoch };
  }

  // S0's one-time corpus attestation was intentionally not installed in
  // production. The current content lifecycle is authoritative instead:
  // each prayer fence is owner-compounded and acknowledges only the exact
  // apply_content_mutation_v1 receipt. A missing historical corpus sidecar
  // is therefore not a reason to hold every newly enabled keepsake forever.
  const currentOwner = getAuthUserId();
  if (!currentOwner) {
    return { hold: { held: true, reason: 'sync-pending' }, localPrayerEpoch: epoch };
  }

  // Every fence: equal sequences, no in-flight, owner scope matching the
  // corpus fence's owner.
  for (const fence of fences) {
    if (fence.inFlightMutationSeq !== null) {
      return { hold: { held: true, reason: 'sync-pending' }, localPrayerEpoch: epoch };
    }
    if (decimalCompare(fence.localMutationSeq, fence.acknowledgedMutationSeq) !== 0) {
      return { hold: { held: true, reason: 'sync-pending' }, localPrayerEpoch: epoch };
    }
    if (fence.ownerScope === null || fence.ownerScope !== currentOwner) {
      return { hold: { held: true, reason: 'sync-pending' }, localPrayerEpoch: epoch };
    }
  }

  return { hold: { held: false }, localPrayerEpoch: epoch };
}

/**
 * Push acknowledgement capture (sync.ts): the seq + digest captured
 * BEFORE a push; only that exact pair may acknowledge after a matching
 * successful response while owner and auth epoch still match.
 */
export interface FencePushCapture {
  prayerId: string;
  seq: string;
  digest: string | null;
  authEpoch: number;
}

export async function captureFenceForPush(prayerId: string): Promise<FencePushCapture | null> {
  const fence = await db.getKeepsakeFence(prayerId);
  if (!fence) return null;
  return {
    prayerId,
    seq: fence.localMutationSeq,
    digest: fence.payloadDigest,
    authEpoch: fence.authEpochAtMutation,
  };
}

export async function acknowledgeFenceAfterPush(capture: FencePushCapture): Promise<void> {
  // A null digest is owner-ambiguous and cannot dispatch an
  // acknowledgement (PC-B section 10).
  if (capture.digest === null) return;
  if (getAuthEpoch() !== capture.authEpoch) return;
  await db
    .acknowledgeKeepsakeFence(capture.prayerId, capture.seq, capture.digest, capture.authEpoch)
    .catch(() => {});
}

/**
 * The complete local active corpus for R4 (PC-B section 10): every local
 * prayer except one covered by an acknowledged delete intent, as
 * `{prayerId, sourceDigest}` pairs under the given owner, sorted by id.
 */
export async function buildLocalCorpus(
  ownerId: string,
  prayers: readonly Prayer[],
): Promise<Array<{ prayerId: string; sourceDigest: string }>> {
  const intents = await db.getAllKeepsakeIntents();
  const deleted = new Set(
    intents
      .filter((i) => i.mutationKind === 'delete' && i.state === 'acknowledged')
      .map((i) => i.prayerId),
  );
  const pairs: Array<{ prayerId: string; sourceDigest: string }> = [];
  for (const prayer of prayers) {
    if (deleted.has(prayer.id)) continue;
    const digest = await computePrayerSourceDigest(ownerId, prayer);
    pairs.push({ prayerId: prayer.id, sourceDigest: digest });
  }
  pairs.sort((a, b) => (a.prayerId < b.prayerId ? -1 : a.prayerId > b.prayerId ? 1 : 0));
  return pairs;
}

// ---------------------------------------------------------------------------
// R4 bidirectional corpus reconciliation (PC-B rev 9, section 10)
// ---------------------------------------------------------------------------

export type CorpusReconcileResult =
  | { ok: true; state: 'matched' }
  | { ok: true; state: 'unresolved'; mismatchCount: number; detail: string[] }
  | { ok: false; code: 'not-signed-in' | 'snapshot-failed' | 'digest-failed' };

interface CorpusItem {
  prayerId: string;
  attestationState: 'unverified' | 'attested';
  sourceDigest: string | null;
  attestationRevision: string;
  reviewDigest: string | null;
  policyRevision: string;
  visibility: 'included' | 'private';
  deletion: 'live';
}

/**
 * The complete BIDIRECTIONAL R4 reconciliation for THIS device context:
 * pages the exact server inventory under one immutable snapshot, builds
 * the complete local active corpus, compares sets both ways, and
 * installs the corpus fence. A server item matches only when it is
 * attested with non-null source + review digests AND one local row has
 * the same id and complete source digest. Server-only, local-only,
 * unverified, duplicate, or digest-different rows leave the fence
 * UNRESOLVED and the affected mutation fences unequal. Only a complete
 * exact match installs `matched`, sets each fence's owner scope, and
 * equalizes its sequences. NO ABSENCE IS INFERRED CLEAN: a mismatch
 * requires an explicit pre-R0 I-10 policy resolution and then a NEW
 * snapshot.
 *
 * Invoked by the R4 rollout tooling (window.__ADIA_KEEPSAKE_R4__, wired
 * in initKeepsakeFences) and by the nonproduction E2E.
 */
export async function runCorpusReconciliation(): Promise<CorpusReconcileResult> {
  const { getAuthUserId } = await import('./epochs');
  const ownerId = getAuthUserId();
  if (!ownerId) return { ok: false, code: 'not-signed-in' };
  const authEpoch = getAuthEpoch();

  const sb = (await import('@/lib/supabase')).getSupabase();
  if (!sb) return { ok: false, code: 'not-signed-in' };

  // Page the server snapshot (first call materializes it).
  const serverItems: CorpusItem[] = [];
  let snapshotId: string | null = null;
  let after: string | null = null;
  let corpusDigest = '';
  let corpusCount = 0;
  for (;;) {
    const { data, error } = await sb.rpc('keepsake_fence_corpus', {
      p_expected_owner: ownerId,
      p_snapshot_id: snapshotId,
      p_after_prayer_id: after,
      p_limit: 100,
    });
    if (error || !data || data.ok !== true) return { ok: false, code: 'snapshot-failed' };
    snapshotId = data.snapshotId as string;
    corpusDigest = data.corpusDigest as string;
    corpusCount = data.corpusCount as number;
    for (const item of data.items as CorpusItem[]) serverItems.push(item);
    if (data.nextAfterPrayerId === null) break;
    // Strictly increasing pages: the cursor must advance.
    if (after !== null && String(data.nextAfterPrayerId) <= after) {
      return { ok: false, code: 'snapshot-failed' };
    }
    after = data.nextAfterPrayerId as string;
  }
  if (serverItems.length !== corpusCount) return { ok: false, code: 'snapshot-failed' };

  // Complete local active corpus under the SAME captured auth epoch.
  if (getAuthEpoch() !== authEpoch) return { ok: false, code: 'snapshot-failed' };
  const prayers = await db.getAllPrayers();
  const localPairs = await buildLocalCorpus(ownerId, prayers);

  // Bidirectional comparison.
  const detail: string[] = [];
  const serverById = new Map<string, CorpusItem>();
  for (const item of serverItems) {
    if (serverById.has(item.prayerId)) detail.push(`duplicate-server:${item.prayerId}`);
    serverById.set(item.prayerId, item);
  }
  const localById = new Map(localPairs.map((p) => [p.prayerId, p.sourceDigest]));
  for (const item of serverItems) {
    const localDigest = localById.get(item.prayerId);
    if (localDigest === undefined) {
      detail.push(`server-only:${item.prayerId}`);
      continue;
    }
    if (
      item.attestationState !== 'attested' ||
      item.sourceDigest === null ||
      item.reviewDigest === null
    ) {
      detail.push(`unverified:${item.prayerId}`);
      continue;
    }
    if (item.sourceDigest !== localDigest) {
      detail.push(`digest-different:${item.prayerId}`);
    }
  }
  for (const pair of localPairs) {
    if (!serverById.has(pair.prayerId)) detail.push(`local-only:${pair.prayerId}`);
  }

  const matched = detail.length === 0;
  // The exact local corpus digest (PC-B section 10):
  // {schema:'adia-local-prayer-corpus-v1',ownerId,items:<sorted pairs>}.
  const { canonicalDigest } = await import('./canonical-json');
  const localCorpusDigest = await canonicalDigest({
    schema: 'adia-local-prayer-corpus-v1',
    ownerId,
    items: localPairs,
  });
  await db.setKeepsakeCorpusFence({
    fenceVersion: 1,
    ownerScope: ownerId,
    authEpoch,
    snapshotId: snapshotId as string,
    serverCorpusCount: corpusCount,
    serverCorpusDigest: corpusDigest,
    localCorpusCount: localPairs.length,
    localCorpusDigest,
    mismatchCount: detail.length,
    state: matched ? 'matched' : 'unresolved',
  });

  if (!matched) {
    return { ok: true, state: 'unresolved', mismatchCount: detail.length, detail };
  }

  // Exact match: set every matching fence's owner scope + equalize its
  // sequences with the verified digest.
  for (const pair of localPairs) {
    await db.resolveKeepsakeFence(pair.prayerId, ownerId, pair.sourceDigest);
  }
  await db.bumpKeepsakeEpoch();
  return { ok: true, state: 'matched' };
}
